import React, { useState, useEffect } from "react";
import { Wallet, Plus, ArrowLeft } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { deposits as api, units, type Unit, type DepositBalance, type DepositUnitSummary, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = { type: "COLLECTED", amount: "", reason: "", transactionDate: new Date().toISOString().slice(0, 10) };

const DepositsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [balance, setBalance] = useState<DepositBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);

  const [summary, setSummary] = useState<DepositUnitSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const refreshSummary = React.useCallback(() => {
    if (!societyId) return;
    setSummaryLoading(true);
    api.getSummary(societyId).then(setSummary).catch(() => {}).finally(() => setSummaryLoading(false));
  }, [societyId]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);

  useEffect(() => {
    if (!societyId) return;
    units.list(societyId).then(page => setUnitList(page.content)).catch(() => {});
  }, [societyId]);

  const refresh = React.useCallback(() => {
    if (!societyId || !unitId) return;
    setLoading(true);
    api.getBalance(societyId, unitId).then(setBalance).catch(() => {}).finally(() => setLoading(false));
  }, [societyId, unitId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !unitId) return;
    setSaving(true); setFormError("");
    try {
      await api.record(societyId, {
        unitId, type: form.type, amount: Number(form.amount), reason: form.reason || undefined,
        transactionDate: form.transactionDate,
      });
      setShowForm(false); setForm(INIT);
      refresh();
      refreshSummary();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to record transaction.");
    } finally { setSaving(false); }
  };

  const selectedSummary = summary.find(s => s.unitId === unitId);

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Damage Deposits
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            Track security deposit collection, deductions, and refunds per unit — money held from residents/tenants against damage to the unit or common areas, returned (minus any deductions) when they move out.
          </p>
        </div>
        {unitId && (
          <button onClick={() => { setFormError(""); setForm(INIT); setShowForm(true); }} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Record Transaction
          </button>
        )}
      </div>

      {!unitId ? (
        <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
          <DataGrid
            columns={[
              { key: "unit", header: "Unit", cellStyle: { fontWeight: 500 }, render: (s: DepositUnitSummary) => s.unitNumber },
              { key: "block", header: "Block", cellStyle: { color: "var(--nx-text-secondary)" }, render: (s: DepositUnitSummary) => s.blockName || "—" },
              { key: "resident", header: "Resident", cellStyle: { fontWeight: 500 }, render: (s: DepositUnitSummary) => s.residentName || "—" },
              { key: "phone", header: "Mobile Number", cellStyle: { color: "var(--nx-text-secondary)" }, render: (s: DepositUnitSummary) => s.residentPhone || "—" },
              {
                key: "balance", header: "Balance",
                cellStyle: { textAlign: "right", fontWeight: 600 },
                headerStyle: { textAlign: "right" },
                render: (s: DepositUnitSummary) => `₹${s.balance.toLocaleString("en-IN")}`,
              },
              {
                key: "actions", header: "",
                render: (s: DepositUnitSummary) => (
                  <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setUnitId(s.unitId)}>
                    View / Manage
                  </button>
                ),
              },
            ]}
            rows={summary}
            rowKey={s => s.unitId}
            loading={summaryLoading}
            emptyIcon={<Wallet size={32} style={{ color: "var(--nx-text-muted)" }} />}
            emptyText="No units found."
          />
        </div>
      ) : loading ? (
        <div style={{ color: "var(--nx-text-muted)", fontSize: 13 }}>Loading…</div>
      ) : balance ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <button onClick={() => setUnitId("")} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 6 }}>
              <ArrowLeft size={13} /> All Units
            </button>
            <div style={{ fontSize: 13, color: "var(--nx-text-secondary)" }}>
              <strong style={{ color: "var(--nx-text-primary)" }}>{selectedSummary?.unitNumber ?? unitList.find(u => u.id === unitId)?.unitNumber}</strong>
              {selectedSummary?.blockName ? ` · ${selectedSummary.blockName}` : ""}
              {selectedSummary?.residentName ? ` · ${selectedSummary.residentName}` : ""}
              {selectedSummary?.residentPhone ? ` · ${selectedSummary.residentPhone}` : ""}
            </div>
          </div>
          <div className="nx-card" style={{ padding: 20, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <Wallet size={24} style={{ color: "var(--nx-accent)" }} />
            <div>
              <div style={{ fontSize: 11, color: "var(--nx-text-muted)", textTransform: "uppercase" }}>Current Balance</div>
              <div style={{ fontSize: 24, fontWeight: 700 }}>₹{balance.balance.toLocaleString("en-IN")}</div>
            </div>
          </div>
          <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--nx-border)" }}>
                  <th style={{ textAlign: "left", padding: 10, color: "var(--nx-text-muted)", fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "left", padding: 10, color: "var(--nx-text-muted)", fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: "left", padding: 10, color: "var(--nx-text-muted)", fontWeight: 600 }}>Reason</th>
                  <th style={{ textAlign: "right", padding: 10, color: "var(--nx-text-muted)", fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {balance.transactions.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "var(--nx-text-muted)" }}>No transactions yet.</td></tr>
                )}
                {balance.transactions.map(t => (
                  <tr key={t.id} style={{ borderBottom: "1px solid var(--nx-border)" }}>
                    <td style={{ padding: 10 }}>{t.transactionDate}</td>
                    <td style={{ padding: 10 }}>{t.type}</td>
                    <td style={{ padding: 10, color: "var(--nx-text-secondary)" }}>{t.reason || "—"}</td>
                    <td style={{ padding: 10, textAlign: "right", fontWeight: 600, color: t.type === "COLLECTED" ? "#22C55E" : "#EF4444" }}>
                      {t.type === "COLLECTED" ? "+" : "-"}₹{t.amount.toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {showForm && (
        <Modal
          title="Record Deposit Transaction"
          submitLabel="Record"
          error={formError}
          isLoading={saving}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Type</label>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={inp}>
              <option value="COLLECTED">Collected</option>
              <option value="DEDUCTED">Deducted</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Amount (₹) *</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} required min="0.01" step="0.01" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Reason</label>
            <input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. wall damage repair" style={inp} />
          </div>
          <div>
            <label style={lbl}>Date *</label>
            <input type="date" value={form.transactionDate} onChange={e => setForm(p => ({ ...p, transactionDate: e.target.value }))} required style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default DepositsPage;
